import React, { useState, useEffect } from 'react';
import { RefreshCw, Plus, Pencil, Trash2, X, GripVertical, ToggleLeft, ToggleRight, ExternalLink, Users, Calendar, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { VirtualCurrencyService, SubscriptionPlan, UserSubscription } from '../services/VirtualCurrencyService';

interface PlanForm {
  name: string;
  billing_interval: 'weekly' | 'monthly' | 'annual';
  price_cents: number;
  token_amount: number;
  stripe_product_id: string;
  stripe_price_id: string;
  tag: string;
  sort_order: number;
  is_active: boolean;
}

const emptyForm: PlanForm = {
  name: '',
  billing_interval: 'monthly',
  price_cents: 799,
  token_amount: 500,
  stripe_product_id: '',
  stripe_price_id: '',
  tag: '',
  sort_order: 0,
  is_active: true,
};

export const AdminSubscriptionPlans: React.FC = () => {
  const coinService = new VirtualCurrencyService();

  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [subscriptions, setSubscriptions] = useState<(UserSubscription & { username?: string; full_name?: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSubs, setLoadingSubs] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [form, setForm] = useState<PlanForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'plans' | 'subscribers'>('plans');

  useEffect(() => {
    loadPlans();
    loadSubscriptions();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const data = await coinService.getAllSubscriptionPlans();
      setPlans(data);
    } catch (err) {
      console.error('Error loading plans:', err);
      setError('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptions = async () => {
    try {
      setLoadingSubs(true);
      const data = await coinService.getAllSubscriptions();
      setSubscriptions(data);
    } catch (err) {
      console.error('Error loading subscriptions:', err);
    } finally {
      setLoadingSubs(false);
    }
  };

  const openCreateModal = () => {
    setEditingPlan(null);
    setForm({ ...emptyForm, sort_order: plans.length });
    setShowModal(true);
    setError(null);
  };

  const openEditModal = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setForm({
      name: plan.name,
      billing_interval: plan.billing_interval,
      price_cents: plan.price_cents,
      token_amount: plan.token_amount,
      stripe_product_id: plan.stripe_product_id,
      stripe_price_id: plan.stripe_price_id,
      tag: plan.tag || '',
      sort_order: plan.sort_order,
      is_active: plan.is_active,
    });
    setShowModal(true);
    setError(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Plan name is required'); return; }
    if (form.token_amount <= 0) { setError('Token amount must be greater than 0'); return; }
    if (form.price_cents <= 0) { setError('Price must be greater than 0'); return; }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        name: form.name.trim(),
        billing_interval: form.billing_interval,
        price_cents: form.price_cents,
        token_amount: form.token_amount,
        stripe_product_id: form.stripe_product_id.trim(),
        stripe_price_id: form.stripe_price_id.trim(),
        tag: form.tag.trim() || null,
        sort_order: form.sort_order,
        is_active: form.is_active,
        updated_at: new Date().toISOString(),
      };

      if (editingPlan) {
        const { error } = await supabase
          .from('subscription_plans')
          .update(payload)
          .eq('id', editingPlan.id);
        if (error) throw error;
        setSuccessMsg('Plan updated successfully');
      } else {
        const { error } = await supabase
          .from('subscription_plans')
          .insert([payload]);
        if (error) throw error;
        setSuccessMsg('Plan created successfully');
      }

      setShowModal(false);
      await loadPlans();
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      console.error('Error saving plan:', err);
      setError(err instanceof Error ? err.message : 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('subscription_plans')
        .delete()
        .eq('id', id);
      if (error) throw error;
      setShowDeleteConfirm(null);
      setSuccessMsg('Plan deleted successfully');
      await loadPlans();
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      console.error('Error deleting plan:', err);
      setError('Failed to delete plan. It may have active subscriptions.');
    }
  };

  const toggleActive = async (plan: SubscriptionPlan) => {
    try {
      const { error } = await supabase
        .from('subscription_plans')
        .update({ is_active: !plan.is_active, updated_at: new Date().toISOString() })
        .eq('id', plan.id);
      if (error) throw error;
      await loadPlans();
    } catch (err) {
      console.error('Error toggling plan:', err);
      setError('Failed to update plan status');
    }
  };

  const formatPrice = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const activeSubCount = subscriptions.filter(s => s.status === 'active').length;
  const cancelledSubCount = subscriptions.filter(s => s.status === 'cancelled').length;

  return (
    <div>
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-4 text-red-500 text-sm">
          {error}
        </div>
      )}

      {successMsg && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-4 text-green-500 text-sm">
          {successMsg}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-kod-purple-700/50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/50 text-xs">Active Subscribers</p>
              <p className="text-xl font-bold text-white mt-1">{activeSubCount}</p>
            </div>
            <Users className="text-teal-400" size={24} />
          </div>
        </div>
        <div className="bg-kod-purple-700/50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/50 text-xs">Cancelled</p>
              <p className="text-xl font-bold text-white mt-1">{cancelledSubCount}</p>
            </div>
            <Clock className="text-orange-400" size={24} />
          </div>
        </div>
        <div className="bg-kod-purple-700/50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/50 text-xs">Plans Configured</p>
              <p className="text-xl font-bold text-white mt-1">{plans.length}</p>
            </div>
            <RefreshCw className="text-teal-400" size={24} />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          <button
            className={`px-3 py-1.5 rounded-lg text-sm ${viewMode === 'plans' ? 'bg-teal-600 text-white' : 'bg-kod-purple-700 text-white/60'}`}
            onClick={() => setViewMode('plans')}
          >
            Plans
          </button>
          <button
            className={`px-3 py-1.5 rounded-lg text-sm ${viewMode === 'subscribers' ? 'bg-teal-600 text-white' : 'bg-kod-purple-700 text-white/60'}`}
            onClick={() => setViewMode('subscribers')}
          >
            Subscribers ({subscriptions.length})
          </button>
        </div>
        {viewMode === 'plans' && (
          <div className="flex items-center gap-3">
            <a
              href="https://dashboard.stripe.com/products"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-kod-purple-700 hover:bg-kod-purple-600 text-white px-4 py-2 rounded-lg flex items-center text-sm transition-colors"
            >
              <ExternalLink size={16} className="mr-2" />
              Stripe
            </a>
            <button
              onClick={openCreateModal}
              className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg flex items-center text-sm transition-colors"
            >
              <Plus size={16} className="mr-2" />
              Add Plan
            </button>
          </div>
        )}
      </div>

      {viewMode === 'plans' ? (
        loading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
          </div>
        ) : plans.length === 0 ? (
          <div className="text-center py-10 text-white/50">
            <RefreshCw size={32} className="mx-auto mb-3 opacity-50" />
            <p>No subscription plans configured yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-kod-purple-600">
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Order</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Interval</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Tokens</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Price</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Stripe IDs</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Tag</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-kod-purple-600/50">
                {plans.map((plan) => (
                  <tr key={plan.id} className="hover:bg-kod-purple-700/50 transition-colors">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center text-white/50">
                        <GripVertical size={14} className="mr-1" />
                        {plan.sort_order}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap font-medium">{plan.name}</td>
                    <td className="px-4 py-4 whitespace-nowrap capitalize">
                      <span className="flex items-center gap-1.5">
                        {plan.billing_interval === 'annual' ? <Calendar size={14} className="text-teal-400" /> : <RefreshCw size={14} className="text-teal-400" />}
                        {plan.billing_interval}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">{plan.token_amount}</td>
                    <td className="px-4 py-4 whitespace-nowrap font-medium text-green-400">
                      {formatPrice(plan.price_cents)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        {plan.stripe_product_id ? (
                          <a
                            href={`https://dashboard.stripe.com/products/${plan.stripe_product_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-teal-400 hover:text-teal-300 font-mono truncate max-w-[140px] inline-flex items-center gap-1"
                          >
                            {plan.stripe_product_id}
                            <ExternalLink size={10} className="flex-shrink-0" />
                          </a>
                        ) : (
                          <span className="text-xs text-white/30">No product ID</span>
                        )}
                        {plan.stripe_price_id ? (
                          <span className="text-xs text-white/40 font-mono truncate max-w-[140px]">{plan.stripe_price_id}</span>
                        ) : (
                          <span className="text-xs text-white/30">No price ID</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {plan.tag ? (
                        <span className="bg-teal-500/20 text-teal-400 text-xs px-2 py-1 rounded-full font-medium">{plan.tag}</span>
                      ) : (
                        <span className="text-white/30 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <button onClick={() => toggleActive(plan)} className="flex items-center gap-1.5">
                        {plan.is_active ? (
                          <><ToggleRight size={20} className="text-green-400" /><span className="text-xs text-green-400">Active</span></>
                        ) : (
                          <><ToggleLeft size={20} className="text-white/30" /><span className="text-xs text-white/30">Inactive</span></>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEditModal(plan)} className="p-1.5 text-white/50 hover:text-white transition-colors rounded hover:bg-kod-purple-600">
                          <Pencil size={15} />
                        </button>
                        <button onClick={() => setShowDeleteConfirm(plan.id)} className="p-1.5 text-white/50 hover:text-red-400 transition-colors rounded hover:bg-red-500/10">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        loadingSubs ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
          </div>
        ) : subscriptions.length === 0 ? (
          <div className="text-center py-10 text-white/50">
            <Users size={32} className="mx-auto mb-3 opacity-50" />
            <p>No subscriptions yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-kod-purple-600">
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Plan</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Platform</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Started</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Next Renewal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-kod-purple-600/50">
                {subscriptions.map((sub) => (
                  <tr key={sub.id} className="hover:bg-kod-purple-700/50 transition-colors">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div>
                        <div className="font-medium">{sub.full_name || sub.username || sub.user_id.slice(0, 8) + '...'}</div>
                        {sub.username && sub.full_name && <div className="text-xs text-white/50">@{sub.username}</div>}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap capitalize">{sub.plan_type}</td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-xs bg-kod-purple-600 px-2 py-1 rounded capitalize">{sub.platform || 'web'}</span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        sub.status === 'active' ? 'bg-green-500/20 text-green-400' :
                        sub.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                        sub.status === 'expired' ? 'bg-gray-500/20 text-gray-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {sub.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-white/70">
                      {sub.started_at ? new Date(sub.started_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-white/70">
                      {sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-kod-purple-800 rounded-xl p-6 max-w-lg w-full shadow-2xl border border-kod-purple-600/50">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">
                {editingPlan ? 'Edit Subscription Plan' : 'New Subscription Plan'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-white/50 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Plan Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., Monthly"
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Billing Interval</label>
                <select
                  className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  value={form.billing_interval}
                  onChange={(e) => setForm(prev => ({ ...prev, billing_interval: e.target.value as 'weekly' | 'monthly' | 'annual' }))}
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Token Amount</label>
                  <input
                    type="number"
                    min="1"
                    className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    value={form.token_amount}
                    onChange={(e) => setForm(prev => ({ ...prev, token_amount: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Price (cents)</label>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      value={form.price_cents}
                      onChange={(e) => setForm(prev => ({ ...prev, price_cents: parseInt(e.target.value) || 0 }))}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 text-sm">
                      {formatPrice(form.price_cents)}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  Stripe Product ID
                  <a href="https://dashboard.stripe.com/products" target="_blank" rel="noopener noreferrer" className="inline-flex items-center ml-2 text-teal-400 hover:text-teal-300 text-xs">
                    Open Stripe <ExternalLink size={10} className="ml-1" />
                  </a>
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 font-mono text-sm"
                  placeholder="prod_..."
                  value={form.stripe_product_id}
                  onChange={(e) => setForm(prev => ({ ...prev, stripe_product_id: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Stripe Price ID (recurring)</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 font-mono text-sm"
                  placeholder="price_..."
                  value={form.stripe_price_id}
                  onChange={(e) => setForm(prev => ({ ...prev, stripe_price_id: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Tag (optional)</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="e.g., BEST VALUE"
                    value={form.tag}
                    onChange={(e) => setForm(prev => ({ ...prev, tag: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1">Sort Order</label>
                  <input
                    type="number"
                    min="0"
                    className="w-full px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    value={form.sort_order}
                    onChange={(e) => setForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 pt-1">
                <button onClick={() => setForm(prev => ({ ...prev, is_active: !prev.is_active }))} className="flex items-center gap-2">
                  {form.is_active ? (
                    <ToggleRight size={24} className="text-green-400" />
                  ) : (
                    <ToggleLeft size={24} className="text-white/30" />
                  )}
                  <span className={`text-sm ${form.is_active ? 'text-green-400' : 'text-white/50'}`}>
                    {form.is_active ? 'Active' : 'Inactive'}
                  </span>
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mt-4 text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowModal(false); setError(null); }}
                className="px-4 py-2 bg-kod-purple-700 hover:bg-kod-purple-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`px-4 py-2 rounded-lg text-white transition-colors ${
                  saving ? 'bg-teal-600/50 cursor-not-allowed' : 'bg-teal-600 hover:bg-teal-700'
                }`}
              >
                {saving ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </div>
                ) : editingPlan ? 'Update Plan' : 'Create Plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-kod-purple-800 rounded-xl p-6 max-w-sm w-full shadow-2xl border border-kod-purple-600/50">
            <h3 className="text-lg font-bold mb-3">Delete Plan</h3>
            <p className="text-white/70 text-sm mb-6">
              Are you sure you want to delete this subscription plan? Active subscribers will not be affected, but no new subscriptions can be created.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 bg-kod-purple-700 hover:bg-kod-purple-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
